var __jsx = React.createElement;
import React, { useRef, useEffect, useState } from "react";
import classNames from "classnames";

/**
 * SlidingText - A component that automatically detects text overflow and provides
 * smooth sliding animation on hover to reveal the full content.
 *
 * @example
 * <SlidingText
 *   text="Very long text that will slide on hover"
 *   containerClassName="w-64 h-8 flex items-center"
 *   speed="normal"
 *   direction="left"
 * />
 */

var SlidingText = function SlidingText(_ref) {
  var _textRef$current, _containerRef$current, _textRef$current2, _containerRef$current2;
  var text = _ref.text,
    _ref$className = _ref.className,
    className = _ref$className === void 0 ? "" : _ref$className,
    _ref$containerClassNa = _ref.containerClassName,
    containerClassName = _ref$containerClassNa === void 0 ? "" : _ref$containerClassNa,
    _ref$direction = _ref.direction,
    direction = _ref$direction === void 0 ? "left" : _ref$direction,
    _ref$speed = _ref.speed,
    speed = _ref$speed === void 0 ? "normal" : _ref$speed,
    _ref$delay = _ref.delay,
    delay = _ref$delay === void 0 ? 0 : _ref$delay,
    children = _ref.children;
  // Component automatically detects text overflow and applies sliding animation on hover
  // Ensure container has fixed width and overflow-hidden for proper functionality
  var containerRef = useRef(null);
  var textRef = useRef(null);
  var _useState = useState(false),
    isOverflowing = _useState[0],
    setIsOverflowing = _useState[1];
  var _useState2 = useState(false),
    isHovered = _useState2[0],
    setIsHovered = _useState2[1];

  // Check if text is overflowing
  useEffect(function () {
    var checkOverflow = function checkOverflow() {
      if (containerRef.current && textRef.current) {
        var containerWidth = containerRef.current.offsetWidth;
        var textWidth = textRef.current.scrollWidth;
        var isOverflow = textWidth > containerWidth;
        setIsOverflowing(isOverflow);
      }
    };

    // Add a small delay to ensure refs are properly attached
    var timer = setTimeout(checkOverflow, 100);

    // Recheck on window resize
    var handleResize = function handleResize() {
      return checkOverflow();
    };
    window.addEventListener("resize", handleResize);
    return function () {
      clearTimeout(timer);
      window.removeEventListener("resize", handleResize);
    };
  }, [text]);

  // Speed mapping
  var speedMap = {
    slow: "duration-[3000ms]",
    normal: "duration-[2000ms]",
    fast: "duration-[1000ms]"
  };

  // For testing, always show sliding behavior
  var forceSliding = true;

  // If no overflow, just render normal text
  if (!isOverflowing && !forceSliding) {
    return /*#__PURE__*/React.createElement("div", {
      className: classNames("overflow-hidden", containerClassName)
    }, /*#__PURE__*/React.createElement("span", {
      className: className
    }, text));
  }
  return /*#__PURE__*/React.createElement("div", {
    ref: containerRef,
    className: classNames("overflow-hidden cursor-pointer", containerClassName),
    onMouseEnter: function onMouseEnter() {
      setIsHovered(true);
    },
    onMouseLeave: function onMouseLeave() {
      setIsHovered(false);
    }
  }, /*#__PURE__*/React.createElement("span", {
    ref: textRef,
    className: classNames("inline-block whitespace-nowrap transition-transform ease-in-out", speedMap[speed], className),
    style: {
      transitionDelay: "".concat(delay, "ms"),
      transform: isHovered ? direction === "left" ? "translateX(-".concat(Math.max(0, (((_textRef$current = textRef.current) === null || _textRef$current === void 0 ? void 0 : _textRef$current.scrollWidth) || 0) - (((_containerRef$current = containerRef.current) === null || _containerRef$current === void 0 ? void 0 : _containerRef$current.offsetWidth) || 0)), "px)") : "translateX(".concat(Math.max(0, (((_textRef$current2 = textRef.current) === null || _textRef$current2 === void 0 ? void 0 : _textRef$current2.scrollWidth) || 0) - (((_containerRef$current2 = containerRef.current) === null || _containerRef$current2 === void 0 ? void 0 : _containerRef$current2.offsetWidth) || 0)), "px)") : "translateX(0)"
    },
    onMouseEnter: function onMouseEnter() {
      setIsHovered(true);
    },
    onMouseLeave: function onMouseLeave() {
      setIsHovered(false);
    }
  }, children || text));
};
export default SlidingText;